import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/notificationService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread'
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications(100, filter === 'unread');
      if (response.success) {
        setNotifications(response.notifications || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await notificationService.markAsRead(notificationId);
      if (response.success) {
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
        );
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      const response = await notificationService.markAllAsRead();
      if (response.success) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }
    
    if (notification.orderId) {
      navigate(`/tracking/${notification.orderId}`);
    } else {
      navigate('/dashboard');
    }
  };

  const formatDate = (date) => {
    const notificationDate = new Date(date);
    return notificationDate.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getNotificationIcon = (type) => {
    const icons = {
      price_defined: '💰',
      order_shipped: '🚚',
      order_delivered: '✅',
      payment_received: '💳',
      payment_simulated: '💳',
      order_cancelled: '❌',
      status_updated: '📦',
      tracking_updated: '📦',
    };
    return icons[type] || '🔔';
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div>
      <Navbar />

      <div className="container">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <h1 className="dashboard-title">🔔 Mes Notifications</h1>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingAll}
                className="btn btn-secondary"
                style={{ fontSize: '14px' }}
              >
                {markingAll ? 'Traitement...' : `Marquer tout comme lu (${unreadCount})`}
              </button>
            )}
          </div>

          {/* Filtres */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <button
              onClick={() => setFilter('all')}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                background: filter === 'all' ? '#FF0000' : '#f0f0f0',
                color: filter === 'all' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: filter === 'all' ? 'bold' : 'normal',
              }}
            >
              Toutes ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                background: filter === 'unread' ? '#FF0000' : '#f0f0f0',
                color: filter === 'unread' ? 'white' : '#333',
                cursor: 'pointer',
                fontWeight: filter === 'unread' ? 'bold' : 'normal',
              }}
            >
              Non lues ({unreadCount})
            </button>
          </div>

          {/* Liste des notifications */}
          {loading ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p>Chargement des notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#666', fontSize: '18px' }}>
                {filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '15px' }}>
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className="card"
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: notification.read ? 'white' : '#f8f9ff',
                    border: notification.read ? '1px solid #e0e0e0' : '2px solid #FF0000',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                    <div style={{ fontSize: '32px' }}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: '#FF0000',
                            }}
                          />
                        )}
                      </div>
                      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
                        {notification.message}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                    {!notification.read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification._id);
                        }}
                        style={{
                          padding: '5px 10px',
                          border: 'none',
                          borderRadius: '4px',
                          background: '#f0f0f0',
                          color: '#666',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Marquer comme lu
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Notifications;

